// src/pages/UnpaidStudents.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Search } from 'lucide-react';
import SummaryApi from '../common/index';
const UnpaidStudents = () => {
  const [students, setStudents] = useState([]);
  const [billings, setBillings] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20; // changed to 20 records per page
  const navigate = useNavigate();

  const fetchStudents = async () => {
    try {
      const adminId = sessionStorage.getItem('admin_id');

      if (!adminId) {
        toast.error('Admin ID not found. Please login again.');
        setTableLoading(false);
        return;
      }

      const res = await fetch(SummaryApi.getRegisteredstudentsByConsultantid.url, {
        method: SummaryApi.getRegisteredstudentsByConsultantid.method || 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_id: parseInt(adminId)
        })
      });
      const result = await res.json();

      if (result.status === true && Array.isArray(result.data)) {
        // Format created_date for display
        const formatted = result.data.map(student => ({
          ...student,
          registered_on: student.created_date
            ? new Date(student.created_date)
                .toLocaleString('en-GB', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })
                .replace(',', '')
            : 'N/A',
        }));
        setStudents(formatted);
      } else {
        setStudents([]);
      }
    } catch (err) {
      console.error('Failed to fetch students:', err);
      setStudents([]);
    }
  };

  const fetchBillings = async () => {
    try {
      const adminId = sessionStorage.getItem('admin_id');

      if (!adminId) {
        toast.error('Admin ID not found. Please login again.');
        setTableLoading(false);
        return;
      }

      const res = await fetch(SummaryApi.getstudentAllBillingByconsultantid.url, {
        method: SummaryApi.getstudentAllBillingByconsultantid.method || 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_id: parseInt(adminId)
        })
      });
      const result = await res.json();

      if (result.status === true && Array.isArray(result.data)) {
        setBillings(result.data);
      } else {
        setBillings([]);
      }
    } catch (err) {
      console.error('Failed to fetch billings:', err);
      setBillings([]);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setTableLoading(true);
      await Promise.all([fetchStudents(), fetchBillings()]);
      setTableLoading(false);
    };
    loadData();
  }, []);

  // Date parsing helper (handles DD-MM-YYYY from display or ISO)
  const parseDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return null;
    try {
      // If already ISO format
      if (dateStr.includes('-') && dateStr.length > 10) {
        return new Date(dateStr);
      }
      // DD-MM-YYYY from display
      const [day, month, rest] = dateStr.split(/[-/]/);
      const [year, time] = rest.split(' ');
      return new Date(`${year}-${month}-${day}`);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let studentsWithoutBilling = students.filter(
      student => !billings.some(billing => billing.student_id === student.student_id)
    );

    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      studentsWithoutBilling = studentsWithoutBilling.filter(student =>
        (student.full_name || '').toLowerCase().includes(searchLower) ||
        (student.student_enrollment_number || '').toLowerCase().includes(searchLower) ||
        (student.mobile_number || '').toLowerCase().includes(searchLower)
      );
    }

    // Apply date range filter on registered_on (created_date)
    if (fromDate || toDate) {
      studentsWithoutBilling = studentsWithoutBilling.filter(student => {
        const regDate = parseDate(student.created_date || student.registered_on);
        if (!regDate) return false;
        let match = true;
        if (fromDate) {
          const from = new Date(fromDate);
          from.setHours(0, 0, 0, 0);
          match = match && regDate >= from;
        }
        if (toDate) {
          const to = new Date(toDate);
          to.setHours(23, 59, 59, 999);
          match = match && regDate <= to;
        }
        return match;
      });
    }

    setFilteredStudents(studentsWithoutBilling);
    setCurrentPage(1);
  }, [searchTerm, fromDate, toDate, students, billings]);

  const handleCreateBill = (studentId) => {
    if (!studentId) return toast.error('No student ID');
    const encodedId = btoa(studentId.toString());
    navigate(`/create-studentfee?id=${encodedId}`);
  };

  const displayValue = (value) => {
    if (value === undefined || value === null || value === '' || value === 0 || value === '0') {
      return 'N/A';
    }
    return value;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFromDate('');
    setToDate('');
    setCurrentPage(1);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  const showEmptyState = !tableLoading && currentItems.length === 0;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white shadow-lg rounded-xl p-6 md:p-8 border border-gray-200">
        <h3 className="text-2xl font-bold mb-6 text-center text-blue-900">
          Unpaid Students Report
        </h3>

        {/* Filters Row */}
        <div className="mb-8 flex flex-col sm:flex-row flex-wrap gap-4 items-end justify-center">
          {/* Search */}
          <div className="relative w-full sm:w-80 lg:w-96">
            <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
              <Search size={20} className="text-gray-500" />
            </div>
            <input
              type="text"
              placeholder="Search by name, enrollment or mobile..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-11 pr-4 py-3 text-base border border-gray-300 rounded-xl
                         focus:border-blue-500 focus:ring-2 focus:ring-blue-200/50
                         outline-none transition-all shadow-sm bg-gray-50/50"
            />
          </div>

          {/* From Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200/50"
            />
          </div>

          {/* To Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="px-4 py-2.5 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200/50"
            />
          </div>

          {/* Clear Filters */}
          <div className="flex items-end">
            <button
              onClick={clearFilters}
              className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition"
            >
              Clear Filters
            </button>
          </div>
        </div>

        {tableLoading ? (
          <div className="text-center py-16 text-gray-600 text-lg font-medium">
            Loading students...
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">S.No</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Name</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Enrollment</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Branch</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Year</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Marks</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Mobile</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Registered On</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-gray-700">Action</th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {showEmptyState ? (
                  <tr>
                    <td colSpan={9} className="py-16 px-6 text-center">
                      <div className="flex flex-col items-center justify-center gap-4">
                        <svg
                          className="w-20 h-20 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-4a2 2 0 00-2 2v1a2 2 0 01-2 2H8a2 2 0 01-2-2v-1a2 2 0 00-2-2H4m4 0h8M7 21h10"
                          />
                        </svg>

                        <p className="text-gray-700 text-lg font-medium">
                          No unpaid students found
                        </p>
                        <p className="text-gray-500 text-sm">
                          {searchTerm || fromDate || toDate
                            ? "Try adjusting filters or search term"
                            : "All registered students have fees paid or no students yet"}
                        </p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  currentItems.map((student, index) => (
                    <tr key={student.student_id} className="hover:bg-blue-50/50 transition-colors">
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {(currentPage - 1) * itemsPerPage + index + 1}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">{displayValue(student.full_name)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {displayValue(student.student_enrollment_number)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">{displayValue(student.branch)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{displayValue(student.year)} year</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{displayValue(student.marks)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{displayValue(student.mobile_number)}</td>
                     <td className="px-6 py-4 text-sm text-gray-900 whitespace-nowrap">{displayValue(student.registered_on)}</td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleCreateBill(student.student_id)}
                          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium transition shadow-sm hover:shadow-md"
                        >
                          Pay Bill
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!tableLoading && !showEmptyState && totalPages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-6">
            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition"
            >
              Previous
            </button>

            <span className="text-base font-medium text-gray-700 px-4">
              Page {currentPage} of {totalPages} ({filteredStudents.length} students)
            </span>

            <button
              onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnpaidStudents;